import { useId } from "react";
import { Icon } from "./utils/Status.helpers";
import { STATUS_CONFIG, STATUS_SIZE } from "./config/Status.config";
import { StatusOverflow } from "./common";
import "./Status.scss";

// ============================================================
//  StatusBadge — pill with dot + icon + label
//  Props: status, size ("xs"|"sm"|"md"|"lg"|"xl"), showIcon,
//         iconName (any Iconify icon name — overrides the default),
//         tooltipId, maxWidth, onClick, DEFAULT, etc.
// ============================================================
export const StatusBadge = ({
    status,
    size = STATUS_SIZE.MD,
    showIcon = false,
    iconName,
    tooltipId,
    maxWidth,
    onClick,
    DEFAULT,
    className = "",
    style = {},
    ...otherIconConfig
}) => {
    const generatedId = useId().replace(/:/g, "");
    const targetId = tooltipId || `status-badge-${generatedId}`;

    const uppercaseNaming = status?.replace(/\s+/g, "").toUpperCase();
    const cfg = STATUS_CONFIG[uppercaseNaming] || DEFAULT || otherIconConfig.DEFAULT;

    if (!cfg) return null;

    // icon prop overrides STATUS_CONFIG default; falls back to config icon
    const resolvedIcon = iconName || cfg.iconName;

    const s = {
        xs: { fs: "9px", px: "8px", py: "4px", gap: "4px", iconSize: 10, radius: "4px" },
        sm: { fs: "10px", px: "10px", py: "4px", gap: "5px", iconSize: 12, radius: "5px" },
        md: { fs: "12px", px: "12px", py: "5px", gap: "6px", iconSize: 13, radius: "6px" },
        lg: { fs: "13px", px: "14px", py: "6px", gap: "7px", iconSize: 14, radius: "7px" },
        xl: { fs: "14px", px: "16px", py: "7px", gap: "8px", iconSize: 15, radius: "8px" },
        xxl: { fs: "15px", px: "18px", py: "8px", gap: "9px", iconSize: 17, radius: "9px" },
        xxxl: { fs: "16px", px: "20px", py: "9px", gap: "10px", iconSize: 20, radius: "10px" },
    }[size] || { fs: "12px", px: "12px", py: "5px", gap: "6px", iconSize: 13, radius: "6px" };

    return (
        <span
            id={targetId}
            className={`status-badge ${className}`}
            onClick={onClick}
            style={{
                display: "inline-flex",
                alignItems: "center",
                fontWeight: cfg.fw || 600,
                letterSpacing: "0.02em",
                color: cfg.color,
                background: cfg.bg,
                border: `1.5px solid ${cfg.border}`,
                lineHeight: 1.5,
                maxWidth: maxWidth || "100%",
                minWidth: 0,
                boxSizing: "border-box",
                cursor: onClick ? "pointer" : "default",
                "--status-fs": s.fs,
                "--status-px": s.px,
                "--status-py": s.py,
                "--status-gap": s.gap,
                "--status-radius": s.radius,
                ...style,
            }}
        >
            {/* icon: custom prop → config default → nothing if showIcon=false */}
            {showIcon && (
                <Icon
                    iconName={resolvedIcon}
                    size={s.iconSize}
                    style={{ flexShrink: 0 }}
                    {...otherIconConfig}
                />
            )}
            <StatusOverflow label={cfg.label} targetId={targetId} />
        </span>
    );
};